import { SHARED_SPREADS, isSharedSpreadSlug } from "../../../../shared-pricing/spreads";

export type PlanCheckoutMode = "credits" | "subscription";

export type PlanCheckoutVariant =
  | "star"
  | "constellation"
  | "galaxy"
  | "luna"
  | "sol";

export type PlanCheckout = {
  mode: PlanCheckoutMode;
  variant: PlanCheckoutVariant;
};

export type MarketingHandoff = {
  source: string;
  medium: string;
  placement: string;
  pageType: string;
  contentSlug?: string;
  intent?: string;
  preferredSpread?: string;
  planVariant?: string;
  planMode?: PlanCheckoutMode;
};

const STORAGE_KEY = "marketing_handoff";
const CHECKOUT_STORAGE_KEY = "marketing_plan_checkout";
const AUTH_RETURN_KEY = "auth_return_path";

const VARIANTS_BY_MODE: Record<PlanCheckoutMode, ReadonlySet<string>> = {
  credits: new Set(["star", "constellation", "galaxy"]),
  subscription: new Set(["luna", "sol"]),
};

const MARKETING_SEARCH_KEYS = [
  "source",
  "medium",
  "placement",
  "pageType",
  "contentSlug",
  "intent",
  "preferredSpread",
  "planVariant",
  "planMode",
  "utm_source",
  "utm_medium",
  "utm_placement",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

function readParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value ? value.slice(0, 120) : undefined;
}

function isPlanCheckoutMode(value: string): value is PlanCheckoutMode {
  return value === "credits" || value === "subscription";
}

function resolvePlanCheckout(
  planMode: string | undefined,
  planVariant: string | undefined,
): PlanCheckout | null {
  const variant = planVariant?.replace(/[^a-z0-9_-]/gi, "").slice(0, 40);
  if (!variant) {
    return null;
  }

  const explicitMode = planMode && isPlanCheckoutMode(planMode) ? planMode : undefined;
  const mode =
    explicitMode ??
    (VARIANTS_BY_MODE.credits.has(variant)
      ? "credits"
      : VARIANTS_BY_MODE.subscription.has(variant)
        ? "subscription"
        : undefined);

  if (!mode || !VARIANTS_BY_MODE[mode].has(variant)) {
    return null;
  }

  return { mode, variant: variant as PlanCheckoutVariant };
}

function readStoredCheckout(): PlanCheckout | null {
  const raw = sessionStorage.getItem(CHECKOUT_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  const parsed = JSON.parse(raw) as Partial<PlanCheckout>;
  return resolvePlanCheckout(parsed.mode, parsed.variant);
}

function writeStoredCheckout(checkout: PlanCheckout | null): void {
  if (!checkout) {
    sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkout));
}

export function parseMarketingHandoff(search: string): MarketingHandoff | null {
  const params = new URLSearchParams(search);
  if (params.get("source") !== "landing") {
    return null;
  }

  const placement = readParam(params, "placement");
  const pageType = readParam(params, "pageType");
  if (!placement || !pageType) {
    return null;
  }

  const preferredSpread = readParam(params, "preferredSpread");
  const checkout = resolvePlanCheckout(
    readParam(params, "planMode"),
    readParam(params, "planVariant"),
  );

  return {
    source: "landing",
    medium: readParam(params, "medium") ?? "cta",
    placement,
    pageType,
    contentSlug: readParam(params, "contentSlug"),
    intent: readParam(params, "intent"),
    preferredSpread:
      isSharedSpreadSlug(preferredSpread)
        ? preferredSpread
        : undefined,
    planVariant: checkout?.variant,
    planMode: checkout?.mode,
  };
}

export function stripMarketingSearchParams(search: string): string {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  const params = new URLSearchParams(normalized);
  let changed = false;

  for (const key of MARKETING_SEARCH_KEYS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }

  if (!changed) {
    return search;
  }

  const next = params.toString();
  return next ? `?${next}` : "";
}

export function storeMarketingHandoff(handoff: MarketingHandoff | null): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (!handoff) {
      sessionStorage.removeItem(STORAGE_KEY);
      writeStoredCheckout(null);
      return;
    }

    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handoff));
    writeStoredCheckout(
      resolvePlanCheckout(handoff.planMode, handoff.planVariant),
    );
  } catch {
    // Authentication should still work when storage is unavailable.
  }
}

export function consumeMarketingHandoffFromLocation(): void {
  if (typeof window === "undefined") {
    return;
  }

  const { search, pathname, hash } = window.location;
  if (!search) {
    return;
  }

  const handoff = parseMarketingHandoff(search);
  if (handoff) {
    storeMarketingHandoff(handoff);
  }

  const nextSearch = stripMarketingSearchParams(search);
  if (nextSearch === search) {
    return;
  }

  window.history.replaceState(null, "", `${pathname}${nextSearch}${hash}`);
}

export function getStoredMarketingHandoff(): MarketingHandoff | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as MarketingHandoff;
    return parsed?.source === "landing" &&
      typeof parsed.placement === "string" &&
      typeof parsed.pageType === "string"
      ? parsed
      : null;
  } catch {
    return null;
  }
}

export function getPreferredSpreadName(
  handoff: MarketingHandoff | null = getStoredMarketingHandoff(),
): string | undefined {
  const slug = handoff?.preferredSpread;
  return isSharedSpreadSlug(slug) ? SHARED_SPREADS[slug].appName : undefined;
}

export function getHandoffDestination(
  handoff: MarketingHandoff | null,
): "/ask" | "/pricing" {
  return handoff?.planVariant ? "/pricing" : "/ask";
}

export function storePlanCheckout(checkout: PlanCheckout): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    writeStoredCheckout(resolvePlanCheckout(checkout.mode, checkout.variant));
  } catch {
    // The pricing page can still be opened manually.
  }
}

export function peekPlanCheckout(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return readStoredCheckout() !== null;
  } catch {
    return false;
  }
}

function isSafeAuthReturnPath(path: string): boolean {
  return path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

/** Remembers an in-app path to open after Kinde returns to /ask. */
export function storeAuthReturnPath(path: string): void {
  if (typeof window === "undefined" || !isSafeAuthReturnPath(path)) {
    return;
  }

  try {
    sessionStorage.setItem(AUTH_RETURN_KEY, path);
  } catch {
    // Login can continue; the OAuth callback still lands on /ask.
  }
}

/** Reads and clears the post-login path so it cannot redirect on a later visit. */
export function consumeAuthReturnPath(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const path = sessionStorage.getItem(AUTH_RETURN_KEY);
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    return path && isSafeAuthReturnPath(path) ? path : null;
  } catch {
    return null;
  }
}

/** Reads and clears the landing pay intent so a refresh cannot open a second invoice. */
export function consumePlanCheckout(): PlanCheckout | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const checkout = readStoredCheckout();
    sessionStorage.removeItem(CHECKOUT_STORAGE_KEY);
    return checkout;
  } catch {
    return null;
  }
}
